<template>
  <component :is="to ? 'router-link' : 'a'" v-bind="linkAttrs" class="tu-program-card tu-browse-card">
    <div v-if="media" class="tpc-media">
      <img v-if="image && !imageFailed" :src="image" alt="" loading="lazy" decoding="async" @error="imageFailed = true" />
      <span v-if="credential" class="tu-credential-badge tpc-credential">{{ credential }}</span>
    </div>
    <div class="tpc-body">
      <!-- Same order as the program page: credential, program, then who offers it -->
      <div v-if="!compact" class="tpc-top">
        <span v-if="credential" class="tu-credential-badge tpc-credential-inline">{{ credential }}</span>
        <h3 class="tpc-title tpc-title--lead" :title="program.title">{{ program.title }}</h3>
        <div class="tpc-offered">
          <span class="tpc-offered-label">Offered by</span>
          <div class="tpc-offered-row">
            <span class="tpc-school-logo" aria-hidden="true">
              <img v-if="schoolLogo && !logoFailed" :src="schoolLogo" alt="" loading="lazy" @error="logoFailed = true" />
              <tu-destination-icon v-else kind="schools" />
            </span>
            <div class="tpc-school-copy">
              <strong class="tpc-school-name" :title="school.name">{{ school.name }}</strong>
              <p v-if="schoolLocation" class="tu-place"><tu-ui-icon name="location" :size="14" />{{ schoolLocation }}</p>
            </div>
          </div>
        </div>
      </div>
      <h3 v-if="compact" class="tpc-title">{{ program.title }}</h3>
      <dl v-if="details.length" class="tpc-details">
        <div v-for="detail in details.slice(0, 2)" :key="detail.label"><dt>{{ detail.label }}</dt><dd>{{ detail.value }}</dd></div>
      </dl>
      <div v-if="hasRealAdmission" class="tpc-admission">
        <span class="tpc-admission-label">{{ admission.label }}</span>
        <strong class="tpc-admission-value">{{ admission.value }}</strong>
      </div>
      <div v-else class="tpc-admission tpc-admission--muted">
        <span class="tpc-admission-label">Application deadline</span>
        <strong class="tpc-admission-value">Confirm with school</strong>
      </div>
      <span class="tu-card-action"><span class="tu-card-action-label">View program</span> <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></span>
    </div>
  </component>
</template>

<script>
import { programAdmission, programCardDetails } from '@/common/tuProgram';
import TuUiIcon from '@/tu/components/UiIcon.vue';
import TuDestinationIcon from '@/tu/components/DestinationIcon.vue';

/**
 * Program card for the student portal. The same card as transitionu.health
 * (components/Tu/ProgramCard.vue, school lead variant): credential, program title,
 * "Offered by" school row, two details on a light blue panel, the admission fact,
 * one "View program" action.
 */
export default {
  name: 'UniProgramCard',
  components: { TuUiIcon, TuDestinationIcon },
  props: {
    // A uni-offered-program row: { program: {...}, tuition_total, duration_months, ... }
    offering: { type: Object, required: true },
    school: { type: Object, required: true },
    href: { type: String, default: '#' },
    /** in-portal route; when set the card stays inside the portal */
    to: { type: [Object, String], default: null },
    /** false drops the cover image (directory grids) */
    media: { type: Boolean, default: true },
    /** On the school's own page the school row is redundant: lead with the program title */
    compact: { type: Boolean, default: false },
  },
  data() {
    return { imageFailed: false, logoFailed: false, now: Date.now() };
  },
  computed: {
    linkAttrs() {
      return this.to ? { to: this.to } : { href: this.href, target: '_blank', rel: 'noopener' };
    },
    program() {
      return this.offering.program || {};
    },
    credential() {
      return this.program.abbr || '';
    },
    image() {
      return this.program.image_url || this.school.campus_img_url || '';
    },
    schoolLogo() {
      return this.school.small_logo_url || this.school.logo_url || '';
    },
    schoolLocation() {
      return [this.school.city, this.school.state].filter(Boolean).join(', ');
    },
    admission() {
      return programAdmission(this.offering, this.now);
    },
    hasRealAdmission() {
      return !!(this.admission && this.admission.value && this.admission.value !== 'Confirm with school');
    },
    details() {
      return programCardDetails(this.offering);
    },
  },
};
</script>

<style scoped>
/* Verbatim from transition_uni_listings components/Tu/ProgramCard.vue */
.tu-program-card { display: flex; flex-direction: column; min-width: 0; height: 100%; overflow: hidden; background: var(--tu-surface); border-radius: 12px; border: 1px solid rgba(24, 24, 24, 0.1); box-sizing: border-box; box-shadow: none; color: var(--tu-ink) !important; text-decoration: none !important; transition: transform 0.16s ease, box-shadow 0.16s ease; }
.tu-program-card:hover { transform: translateY(-1px); box-shadow: var(--tu-shadow-lift); }
.tu-program-card:focus-visible { outline: 2px solid var(--tu-blue); outline-offset: 4px; }
.tpc-media { position: relative; flex: none; height: 144px; background: var(--tu-surface-2); }
.tpc-media img { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center 20%; }
.tpc-body { display: flex; flex-direction: column; flex: 1; gap: 14px; padding: 22px; }
.tpc-body > .tu-card-action { margin-top: auto; }
.tpc-credential { position: absolute; left: 20px; bottom: 12px; }
.tpc-body h3 { margin: 0; font-size: 17px; font-weight: 700; line-height: 1.25; color: var(--tu-ink); }
.tpc-title { font-size: 18px; }
.tpc-body p { margin: 0; font-size: 14px; line-height: 1.5; color: var(--tu-ink-soft); }
.tpc-top { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
.tpc-title--lead { font-size: 19px; line-height: 1.22; letter-spacing: -0.01em; min-height: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.tpc-offered { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; width: 100%; min-width: 0; margin-top: 6px; }
.tpc-offered-row { display: flex; align-items: center; gap: 10px; width: 100%; min-width: 0; }
.tpc-offered-label { flex: none; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: var(--tu-ink-mute); }
.tpc-school-copy { min-width: 0; flex: 1; }
.tpc-school-name { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; font-size: 14px; line-height: 1.35; font-weight: 700; color: var(--tu-ink); }
.tpc-offered .tpc-school-copy p { margin: 2px 0 0; font-size: 12.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; display: flex; align-items: center; gap: 4px; }
.tpc-credential-inline { font-size: 11px; padding: 4px 8px; }
.tpc-school-logo { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; border-radius: 10px; overflow: hidden; color: var(--tu-blue); background: #fff; padding: 4px; box-sizing: border-box; box-shadow: inset 0 0 0 1px var(--tu-line); }
.tpc-school-logo img { width: 100%; height: 100%; object-fit: contain; }
.tpc-school-logo ::v-deep svg { width: 22px; height: 22px; }
.tpc-admission { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 12px; border-radius: 10px; background: var(--tu-blue-tint); }
.tpc-admission-label { font-size: 12.5px; font-weight: 600; color: var(--tu-blue-deep); }
.tpc-admission-value { font-size: 14px; font-weight: 700; color: var(--tu-ink); }
.tpc-admission--muted { background: var(--tu-surface-2); }
.tpc-admission--muted .tpc-admission-label { color: var(--tu-ink-mute); }
.tpc-admission--muted .tpc-admission-value { font-weight: 500; color: var(--tu-ink-mute); }
.tpc-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; margin: 0; padding: 14px 16px; border-radius: 12px; background: var(--tu-blue-tint); }
.tpc-details > div { min-width: 0; }
.tpc-details dt { display: block; font-size: 12.5px; font-weight: 500; line-height: 1.4; color: var(--tu-ink-soft); }
.tpc-details dd { margin: 4px 0 0; font-size: 17px; line-height: 1.3; font-weight: 700; color: var(--tu-ink); overflow-wrap: anywhere; }
.tpc-body > .tu-card-action { border-top: 1px solid var(--tu-line-soft); padding-top: 14px; }
@media (max-width: 600px) { .tpc-media { height: 168px; } }
@media (prefers-reduced-motion: reduce) { .tu-program-card { transition: none; } .tu-program-card:hover { transform: none; } }
</style>
